import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchPaginatedArchivedChannelSessions } from 'flow/actions/channels-actions';
import Loading from 'flow/components/ui-components/Loading';
import Tabs from 'flow/components/ui-components/Tabs';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectArchivedChatSessions,
    selectOpenChatSessions,
    selectOwnUserIsMemberOfChannel,
} from 'flow/selectors/channel-selectors';
import { ChannelKey } from 'flow/types/EntityKeys';
import { calcTotalUnreadChannelSessionMessages } from 'flow/utils/pbx-utils';
import { twMerge } from 'tailwind-merge';
import WidgetLatestMessage from './WidgetLatestMessage';

interface Props {
    className?: string;
}

type IssueType = 'ALL' | 'ARCHIVED';

const LiveChatTickets = ({ className }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { entityKey: channelKey } = useParams<{ entityKey: ChannelKey }>();

    const [tabState, setTabState] = useState<IssueType>('ALL');

    const filteredChatSessions = useAppSelector((state) =>
        selectOpenChatSessions(state, channelKey),
    );

    const archivedChatSessions = useAppSelector((state) =>
        selectArchivedChatSessions(state, channelKey),
    );

    const ownUserIsMemberOfChannel = useAppSelector((state) =>
        selectOwnUserIsMemberOfChannel(state, channelKey),
    );

    if (!ownUserIsMemberOfChannel) {
        return null;
    }

    const handleFetchArchivedSessions = (beforeMessageKey: string = null) => {
        dispatch(
            fetchPaginatedArchivedChannelSessions(channelKey, beforeMessageKey),
        );
    };

    const onChangeTab = (value: IssueType) => {
        if (value === 'ARCHIVED') {
            handleFetchArchivedSessions();
        }

        setTabState(value);
    };

    const tabs = [
        {
            key: 'All',
            label: i18next.t('pbx.channels.all_open_issues'),
            badgeCounter:
                calcTotalUnreadChannelSessionMessages(filteredChatSessions),
        },
        {
            key: 'ARCHIVED',
            label: i18next.t('pbx.channels.archived'),
        },
    ];

    const renderIssueType = () => {
        const sessions =
            tabState === 'ARCHIVED'
                ? archivedChatSessions
                : filteredChatSessions;

        if (sessions === undefined || sessions === null) {
            return <Loading />;
        }

        if (sessions.isEmpty()) {
            return (
                <div className="flex items-center gap-3 p-3">
                    <p className="rounded-md border-2 border-gray-300 px-3 py-1 text-xs text-gray-400">
                        {0}
                    </p>
                    <p>
                        {tabState === 'ARCHIVED'
                            ? i18next.t('pbx.channels.no_archived')
                            : i18next.t('pbx.channels.no_issues')}
                    </p>
                </div>
            );
        }

        return sessions
            .valueSeq()
            .map((chatSession) => (
                <WidgetLatestMessage
                    key={chatSession.get('key')}
                    chatSession={chatSession}
                />
            ));
    };

    return (
        <div className={twMerge('flex flex-col gap-4', className)}>
            <Tabs
                tabs={tabs}
                selectedKey={tabState}
                onClick={({ key }) => onChangeTab(key as IssueType)}
            />

            {renderIssueType()}
        </div>
    );
};

export default LiveChatTickets;
